// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils/classnames';
import Swal from 'sweetalert2';
import { z } from 'zod';
import type { ShowModalOptions } from './show-modal';
import styles from '@shared/ui-kit/components/popups/show-modal/modal.module.css';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { type TFunction } from 'i18next';

const inputProps = {
  type: 'text',
  className: styles.input,
};

const emailSchema = z.email();

export const MODAL_TEMPLATES_OPTIONS = (t: TFunction) => ({
  UNLINK_BROKER_ACCOUNT: {
    title: t('unlink-broker.title', '', { ns: 'popups' }),
    content: t('unlink-broker.content', '', { ns: 'popups' }),
    variant: 'overflow',
    icon: <AppIcon icon="Unlink" size="2xl" color="main" />,
    buttons: {
      cancel: {
        show: true,
        text: t('unlink-broker.cancel', '', { ns: 'popups' }),
      },
      confirm: {
        show: true,
        text: t('unlink-broker.confirm', '', { ns: 'popups' }),
      },
    },
  } as ShowModalOptions,
  SAVE_PORTFOLIO: {
    title: t('save-portfolio.title', '', { ns: 'popups' }),
    content: t('save-portfolio.content', '', { ns: 'popups' }),
    variant: 'overflow',
    icon: <AppIcon icon="Save" size="2xl" color="main" />,
    input: {
      placeholder: t('common.name', '', { ns: 'common' }),
      type: 'text',
    },
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.save', '', { ns: 'common' }),
      },
    },
  } as ShowModalOptions,
  EDIT_SAVED_PORTFOLIO: {
    title: t('save-changes.title', '', { ns: 'popups' }),
    content: t('save-changes.content', '', { ns: 'popups' }),
    variant: 'overflow',
    icon: <AppIcon icon="Save" size="2xl" color="main" />,
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.save', '', { ns: 'common' }),
      },
    },
  } as ShowModalOptions,
  EXECUTE_TRADES: {
    title: t('trade-execution.title', '', { ns: 'popups' }),
    variant: 'box',
  } as ShowModalOptions,
  DOWNLOAD_REPORTS: {
    title: t('download-report.title', '', { ns: 'popups' }),
    variant: 'overflow',
    icon: <AppIcon icon="Reporting" size="2xl" color="main" />,
    content: (
      <div className="mt-4 flex flex-col items-center">
        <div className="grid w-full max-w-md grid-cols-2">
          <input
            placeholder={t('common.client-name', '', { ns: 'common' })}
            id="client-name"
            autoComplete="off"
            {...inputProps}
            className={classNames(
              inputProps.className,
              'w-full rounded-b-none rounded-r-none border-b-0 border-r-0',
            )}
          />
          <input
            placeholder={t('common.client-address', '', { ns: 'common' })}
            id="client-address"
            autoComplete="off"
            {...inputProps}
            className={classNames(
              inputProps.className,
              'w-full rounded-b-none rounded-l-none border-b-0',
            )}
          />
          <input
            placeholder={t('common.client-phone', '', { ns: 'common' })}
            id="client-phone"
            autoComplete="off"
            {...inputProps}
            className={classNames(
              inputProps.className,
              'w-full rounded-r-none rounded-t-none border-r-0',
            )}
          />
          <input
            placeholder={t('common.report-name', '', { ns: 'common' })}
            id="report-name"
            autoComplete="off"
            {...inputProps}
            className={classNames(
              inputProps.className,
              'w-full rounded-l-none rounded-t-none',
            )}
          />
        </div>
      </div>
    ),
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.download', '', { ns: 'common' }),
      },
      allowOutsideClick: /* istanbul ignore next */ () => !Swal.isLoading(),
    },
  } as ShowModalOptions,
  DOWNLOAD_RESEARCH_REPORT: {
    title: t('download-report.title', '', { ns: 'popups' }),
    variant: 'overflow',
    icon: <AppIcon icon="Reporting" size="2xl" color="main" />,
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.download', '', { ns: 'common' }),
      },
      allowOutsideClick: /* istanbul ignore next */ () => !Swal.isLoading(),
    },
  } as ShowModalOptions,
  UNHANDLED_ERROR: {
    icon: <AppIcon icon="ExclamationMark" size="2xl" color="main" />,
    title: t('errors.error-title', 'An error occurred', { ns: 'errors' }),
    buttons: {
      cancel: {
        show: true,
        text: t('common.home', 'Home', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.confirm', 'Confirm', { ns: 'common' }),
      },
    },
    didClose: () => {
      window.location.href = '/';
    },
    reverseButtons: false,
    variant: 'overflow',
    allowOutsideClick: false,
  } as ShowModalOptions,
  WARN: {
    icon: <AppIcon icon="Warning" size="2xl" color="main" />,
    variant: 'overflow',
    allowOutsideClick: false,
  },
  CONFIRM_DELETE: {
    icon: <AppIcon icon="ExclamationMark" size="2xl" color="main" />,
    variant: 'overflow',
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.yes-delete', '', { ns: 'common' }),
      },
    },
    reverseButtons: true,
    allowOutsideClick: /* istanbul ignore next */ () => !Swal.isLoading(),
  } as ShowModalOptions,
  INVITE_CITEC_USER_CLIENT: {
    title: t('common.invite-citec-user-client', '', { ns: 'common' }),
    variant: 'overflow',
    icon: <AppIcon icon="Account" size="2xl" color="main" />,
    content: (
      <form id="inviteClientForm">
        <input
          name="first_name"
          placeholder={t('common.first-name', '', { ns: 'common' })}
          id="first_name"
          {...inputProps}
          className={classNames(inputProps.className, 'w-80')}
        />
        <input
          name="second_name"
          placeholder={t('common.second-name', '', { ns: 'common' })}
          id="second_name"
          {...inputProps}
          className={classNames(inputProps.className, 'w-80')}
        />
        <input
          name="first_surname"
          placeholder={t('common.first-surname', '', { ns: 'common' })}
          id="first_surname"
          {...inputProps}
          className={classNames(inputProps.className, 'w-80')}
        />
        <input
          name="second_surname"
          placeholder={t('common.second-surname', '', { ns: 'common' })}
          id="second_surname"
          {...inputProps}
          className={classNames(inputProps.className, 'w-80')}
        />
        <input
          name="email"
          placeholder={t('common.email', '', { ns: 'common' })}
          id="email"
          {...inputProps}
          className={classNames(inputProps.className, 'w-80')}
          type="email"
          autoComplete="email"
          required
        />
        <div id="emailError" className="mt-1 hidden text-sm text-negative">
          {t('invite-client.invalid-email', '', { ns: 'popups' })}
        </div>
      </form>
    ),
    didOpen: /* istanbul ignore next */ popup => {
      const email = popup.querySelector('#email') as HTMLInputElement;
      const errorEl = popup.querySelector('#emailError') as HTMLDivElement;
      const confirmBtn = Swal.getConfirmButton();

      const validateAndUpdate = () => {
        const value = email.value.trim();
        const isValid = value && emailSchema.safeParse(value).success;

        if (confirmBtn) confirmBtn.disabled = !isValid;

        if (value && !isValid) {
          errorEl.classList.remove('hidden');
          email.setAttribute('aria-invalid', 'true');
        } else {
          errorEl.classList.add('hidden');
          email.setAttribute('aria-invalid', 'false');
        }
      };

      if (confirmBtn) confirmBtn.disabled = true;

      email.addEventListener('input', validateAndUpdate);
    },
    buttons: {
      cancel: {
        show: true,
        text: t('common.cancel', '', { ns: 'common' }),
      },
      confirm: {
        show: true,
        text: t('common.confirm', '', { ns: 'common' }),
      },
      allowOutsideClick: /* istanbul ignore next */ () => !Swal.isLoading(),
    },
  } as ShowModalOptions,
});

export const BASE_POPUP: ShowModalOptions = {
  variant: 'box',
  icon: <AppIcon icon="Info" size="md" color="main" />,
  buttons: {
    close: { show: true },
  },
  width: 540,
};
